import React, { Suspense, lazy, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Route, BrowserRouter, Routes, Navigate, useParams } from 'react-router-dom';
import ReduxToastr from 'react-redux-toastr'
import { connect } from 'react-redux';
import theme from '../theme';
import { storePollingInterval } from '../actions/callRequestActions';
import {
    bootstrapCustomer,
    customerSessionExpired,
} from '../actions/customerActions';
import { SESSION_EXPIRED_EVENT } from '../apis/customerSession';
import HasCallRequestRoute from './HasCallRequestRoute';
import HasCallRoute from './HasCallRoute';
import HasCustomerRoute from './HasCustomerRoute';
import CreateCallRequest from './CreateCallRequest';
import Call from './Call';
import WaitingRoom from './WaitingRoom';
import Home from './Home';
import LiveViewer from './LiveViewer';
import PrivacyPolicy from './PrivacyPolicy';
import Account from './Account';
import MyCallRequests from './MyCallRequests';
import MyPurchaseOrders from './MyPurchaseOrders';
import PaymentReturn from './PaymentReturn';
import PaymentPage from './PaymentPage';
import StorePage from './storefront/StorePage';
import ProductDetail from './storefront/ProductDetail';
import CategoryPage from './storefront/CategoryPage';
import CartList from './cart/CartList';
import CartPage from './cart/CartPage';
import CheckoutPage from './cart/CheckoutPage';
import Terms from './Terms';
import Support from './Support';
import CommunityGuidelines from './CommunityGuidelines';
import { Box } from '@mui/material';

import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import '../styles/styles.scss';

// The business landing (ex quierovendermas.com.ar): its own chunk, so buyers
// never download it and it never waits on the app's bundle.
const NegociosPage = lazy(() => import('./negocios/NegociosPage'));

// Wrapper component to pass storeId prop
const CreateCallRequestWrapper = () => {
    const { storeId } = useParams();
    return <CreateCallRequest storeId={storeId} />;
};

function App({ callRequest, storePollingInterval, bootstrapCustomer, customerSessionExpired }) {
    useEffect(() => {
        if(callRequest.callRequest && !callRequest.pollingInterval) {
            storePollingInterval();
        }
    }, [callRequest.callRequest, callRequest.pollingInterval, storePollingInterval]);

    // customer session: silent boot + react to a dead refresh token
    useEffect(() => {
        bootstrapCustomer();

        const onSessionExpired = () => customerSessionExpired();
        window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    }, [bootstrapCustomer, customerSessionExpired]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <BrowserRouter>
                {/* Contenedor principal para empujar el footer abajo */}
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                    
                    {/* El contenido de las rutas toma el espacio restante flexGrow: 1 */}
                    <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        <Routes>
                            <Route
                                path="/:storeId/create-call-request"
                                element={<CreateCallRequestWrapper />}
                            />
                            <Route path="/home" element={<Home />} />
                            {/* Vidriera de la tienda (catálogo read-only; la venta se cierra en la videollamada) */}
                            <Route path="/tienda/:storeId" element={<StorePage />} />
                            <Route path="/categoria/:categoryId" element={<CategoryPage />} />
                            <Route
                                path="/tienda/:storeId/producto/:productId"
                                element={<ProductDetail />}
                            />
                            <Route path="/store/:storeId/live" element={<LiveViewer />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            {/* The URL the store listings link to (Play Console): without
                                it the catch-all below sent reviewers to /home */}
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/support" element={<Support />} />
                            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                            <Route
                                path="/negocios"
                                element={
                                    <Suspense fallback={null}>
                                        <NegociosPage />
                                    </Suspense>
                                }
                            />
                            {/* Pago con Payway (página propia) y retorno del
                                checkout de MercadoPago. Sin guard de sesión: el
                                comprador puede llegar desde el mail. */}
                            <Route
                                path="/pagar/:purchaseOrderId"
                                element={<PaymentPage />}
                            />
                            <Route
                                path="/pago/exito/:purchaseOrderId"
                                element={<PaymentReturn estado="exito" />}
                            />
                            <Route
                                path="/pago/pendiente/:purchaseOrderId"
                                element={<PaymentReturn estado="pendiente" />}
                            />
                            <Route
                                path="/pago/cancelado/:purchaseOrderId"
                                element={<PaymentReturn estado="cancelado" />}
                            />
                            <Route
                                path="/account"
                                element={
                                    <HasCustomerRoute>
                                        <Account />
                                    </HasCustomerRoute>
                                }
                            />
                            <Route
                                path="/account/calls"
                                element={
                                    <HasCustomerRoute>
                                        <MyCallRequests />
                                    </HasCustomerRoute>
                                }
                            />
                            <Route
                                path="/account/orders"
                                element={
                                    <HasCustomerRoute>
                                        <MyPurchaseOrders />
                                    </HasCustomerRoute>
                                }
                            />
                            {/* Carrito y checkout sin videollamada (etapa 3).
                                stayAfterLogin: tras loguearse desde el gate se
                                queda en el carrito en vez de ir al home. */}
                            <Route
                                path="/carrito"
                                element={
                                    <HasCustomerRoute stayAfterLogin>
                                        <CartList />
                                    </HasCustomerRoute>
                                }
                            />
                            <Route
                                path="/carrito/:storeId"
                                element={
                                    <HasCustomerRoute stayAfterLogin>
                                        <CartPage />
                                    </HasCustomerRoute>
                                }
                            />
                            <Route
                                path="/checkout/:storeId"
                                element={
                                    <HasCustomerRoute stayAfterLogin>
                                        <CheckoutPage />
                                    </HasCustomerRoute>
                                }
                            />
                            <Route
                                path="/waiting-room"
                                element={
                                    <HasCallRequestRoute>
                                        <WaitingRoom />
                                    </HasCallRequestRoute>
                                }
                            />
                            <Route
                                path="/call"
                                element={
                                    <HasCallRoute>
                                        <Call />
                                    </HasCallRoute>
                                }
                            />
                            
                            {/* default state when not match found */}
                            <Route path="*" element={<Navigate to="/home" replace />} />
                        </Routes>
                    </Box>

            

                </Box>
            </BrowserRouter>
            <ReduxToastr
                timeOut={4000}
                newestOnTop={false}
                preventDuplicates
                position="top-right"
                transitionIn="fadeIn"
                transitionOut="fadeOut"
                progressBar
                closeOnToastrClick
                confirmOptions={{
                    okText: 'aceptar',
                    cancelText: 'cancelar',
                }}
            /> 
        </ThemeProvider>
    );
}

const mapStateToProps = (state) => {
    return {
        callRequest: state.callRequest,
    };
};

export default connect(
    mapStateToProps,
    {
        storePollingInterval,
        bootstrapCustomer,
        customerSessionExpired,
    } //connect actions creators to the component
)(App);